import { FUSE_COLOURS, fuseColourKey, fuseColourLight, type FuseBox, type FuseEntry, type FuseTable } from "@szerviz/core";
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Logo } from "./Logo";

/**
 * The own fuse sheet (owner, 2026-11-01): Garagevo draws the fuse and relay table found by the
 * AI lookup (a shared "fuseTable" fact) in its own look: per box the location, a schematic of
 * the fuses in number order in the standard blade fuse colours, the colour legend and the
 * table; then the relays and the sources. No picture of the source is copied. "PDF / Print"
 * prints only this sheet on A4 (the browser saves it as PDF).
 */
export function FuseSheet({ table, vehicle, unchecked, status }: {
  table: FuseTable;
  /** The type path, e.g. "Škoda Octavia II 1.9 TDI". */
  vehicle: string;
  /** An AI table nobody confirmed yet: the "AI · check it" badge. */
  unchecked: boolean;
  /** The state badge and the ✓ / ✗ votes on the whole table (not printed). */
  status?: ReactNode;
}) {
  const { t } = useTranslation();
  const { printing, print } = usePrintSheet();
  return (
    <section className="fuse-sheet" aria-label={t("fuse.title")}>
      <div className="fuse-tools">
        {status && <span className="fuse-status">{status}</span>}
        <button type="button" className="fuse-print" onClick={print}>
          {t("fuse.print")}
        </button>
      </div>
      <FuseSheetBody table={table} vehicle={vehicle} unchecked={unchecked} />
      {printing && createPortal(
        <div className="fuse-print-root">
          <FuseSheetBody table={table} vehicle={vehicle} unchecked={unchecked} print />
        </div>,
        document.body,
      )}
    </section>
  );
}

/**
 * "PDF / Print" of an own sheet: a print copy is put at the end of the page and only that is
 * printed (styles.css: body.printing-fuse); each tap is a new run, the copy stays until "afterprint".
 */
export function usePrintSheet(): { printing: boolean; print: () => void } {
  const [run, setRun] = useState(0);
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    if (run === 0) return;
    document.body.classList.add("printing-fuse");
    const done = () => {
      document.body.classList.remove("printing-fuse");
      setPrinting(false);
    };
    window.addEventListener("afterprint", done);
    // once the print copy is in the page
    const timer = window.setTimeout(() => window.print(), 30);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("afterprint", done);
      document.body.classList.remove("printing-fuse");
    };
  }, [run]);
  return { printing, print: () => { setPrinting(true); setRun((r) => r + 1); } };
}

function FuseSheetBody({ table, vehicle, unchecked, print }: { table: FuseTable; vehicle: string; unchecked: boolean; print?: boolean }) {
  const { t } = useTranslation();
  const main = table.sources.find((s) => s.role === "main") ?? table.sources[0];
  // the main source and at most one more: too much information confuses (owner, 2026-11-01)
  const compared = table.sources.filter((s) => s !== main).slice(0, 1);
  return (
    <div className="fuse-body">
      <header className="fuse-head">
        <Logo size={26} />
        <span className="muted small">{t("fuse.kind")}</span>
      </header>
      <h3 className="fuse-title">
        {t("fuse.title")}
        {/* in the program the state badge with the votes says it above the sheet */}
        {unchecked && print && <span className="fuse-ai">{t("fuse.aiCheck")}</span>}
      </h3>
      <p className="fuse-car">{[vehicle, table.years].filter(Boolean).join(", ")}</p>
      <p className="fuse-note">{t("fuse.note")}</p>
      {table.boxes.map((b, i) => (
        <section key={i} className="fuse-box">
          <h4 className="fuse-box-head">{i + 1}. {b.name}</h4>
          {b.location && <p className="fuse-where"><span aria-hidden="true">📍 </span>{b.location}</p>}
          {/* wide on paper and on the computer, fewer columns (bigger fuses) on the phone */}
          <FuseSchematic box={b} idPrefix={`${print ? "p" : "s"}${i}w`} maxCols={17} className="fuse-schematic-wide" />
          {!print && <FuseSchematic box={b} idPrefix={`s${i}n`} maxCols={8} className="fuse-schematic-narrow" />}
          <FuseLegend fuses={b.fuses} />
          <div className="fuse-tables">
            <FuseTableHalf fuses={b.fuses.slice(0, Math.ceil(b.fuses.length / 2))} />
            {b.fuses.length > 1 && <FuseTableHalf fuses={b.fuses.slice(Math.ceil(b.fuses.length / 2))} />}
          </div>
        </section>
      ))}
      {table.relays.length > 0 && (
        <section className="fuse-box">
          <h4 className="fuse-box-head">{table.boxes.length + 1}. {t("fuse.relays")}</h4>
          <table className="fuse-table fuse-relays">
            <thead>
              <tr><th scope="col">{t("fuse.colPosition")}</th><th scope="col">{t("fuse.colRelay")}</th></tr>
            </thead>
            <tbody>
              {table.relays.map((r, i) => (
                <tr key={i}><td className="fuse-pos">{r.position}</td><td>{r.what}</td></tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      <footer className="fuse-foot">
        {t("fuse.drawn")}
        {main && (
          <>
            {" "}{t("fuse.mainSource")} <SourceLink url={main.url} />
          </>
        )}
        {compared.length > 0 && (
          <>
            {"; "}{t("fuse.compared")}{" "}
            {compared.map((s, i) => (
              <span key={s.url}>{i > 0 && ", "}<SourceLink url={s.url} /></span>
            ))}
          </>
        )}
        {". "}
        {t("fuse.checkedBy")}
      </footer>
    </div>
  );
}

export function SourceLink({ url }: { url: string }) {
  let label = url;
  try {
    const u = new URL(url);
    label = u.hostname.replace(/^www\./, "") + (u.pathname.length > 1 ? ` · ${decodeURIComponent(u.pathname.split("/").filter(Boolean).pop() ?? "").replace(/\.[a-z]{2,5}$/i, "").slice(0, 40)}` : "");
  } catch {
    // not a URL: shown as it is
  }
  return <a href={url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">{label}</a>;
}

const W = 34;
const H = 48;
const GAP = 6;

/** The fuses of a box in number order (not the real layout: that is on the source's diagram). */
function FuseSchematic({ box, idPrefix, maxCols, className }: { box: FuseBox; idPrefix: string; maxCols: number; className: string }) {
  const { t } = useTranslation();
  const n = box.fuses.length;
  const rows = Math.max(1, Math.ceil(n / maxCols));
  const cols = Math.max(1, Math.ceil(n / rows));
  const width = cols * (W + GAP) + 24;
  const height = rows * (H + GAP) + 34;
  return (
    <svg className={`fuse-schematic ${className}`} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={box.name} style={{ maxWidth: Math.min(width * 1.25, 760) }}>
      <rect x={2} y={2} width={width - 4} height={height - 4} rx={12} fill="#F2F5F7" stroke="#0E2433" strokeWidth={1.5} />
      {box.fuses.map((f, i) => (
        <Fuse key={`${idPrefix}-${i}`} f={f} x={13 + (i % cols) * (W + GAP)} y={12 + Math.floor(i / cols) * (H + GAP)} />
      ))}
      <text x={width / 2} y={height - 9} textAnchor="middle" fontSize={9} fill="#5B6B78">{t("fuse.schematic")}</text>
    </svg>
  );
}

function Fuse({ f, x, y }: { f: FuseEntry; x: number; y: number }) {
  const key = fuseColourKey(f.amp);
  const fill = key ? FUSE_COLOURS[key]! : "#FFFFFF";
  const ink = fuseColourLight(key) ? "#0E2433" : "#FFFFFF";
  const amp = f.amp ? `${f.amp}A` : "–";
  return (
    <g>
      <rect x={x} y={y} width={W} height={H} rx={5} fill={fill} stroke="#0E2433" strokeWidth={1.1} strokeDasharray={f.amp ? undefined : "3 2"} />
      <rect x={x + 8} y={y + H - 6} width={5} height={8} fill="#9AA7B0" />
      <rect x={x + W - 13} y={y + H - 6} width={5} height={8} fill="#9AA7B0" />
      <text x={x + W / 2} y={y + 20} textAnchor="middle" fontSize={f.no.length > 3 ? 8.5 : f.no.length > 2 ? 10.5 : 13} fontWeight={700} fill={ink}>{f.no}</text>
      <text x={x + W / 2} y={y + 35} textAnchor="middle" fontSize={amp.length > 6 ? 6.5 : amp.length > 4 ? 8.5 : 10} fill={ink}>{amp}</text>
    </g>
  );
}

/** The colours used in the box, smallest first. */
function FuseLegend({ fuses }: { fuses: FuseEntry[] }) {
  const { t } = useTranslation();
  const keys = Object.keys(FUSE_COLOURS).sort((a, b) => Number(a.replace(",", ".")) - Number(b.replace(",", "."))).filter((k) => fuses.some((f) => fuseColourKey(f.amp) === k));
  if (keys.length === 0) return null;
  return (
    <p className="fuse-legend" aria-label={t("fuse.legend")}>
      {keys.map((k) => (
        <span key={k} className="fuse-lg"><span className="fuse-dot" style={{ background: FUSE_COLOURS[k] }} />{t("fuse.amp", { amp: k })}</span>
      ))}
    </p>
  );
}

function FuseTableHalf({ fuses }: { fuses: FuseEntry[] }) {
  const { t } = useTranslation();
  return (
    <table className="fuse-table">
      <thead>
        <tr><th scope="col">{t("fuse.colNo")}</th><th scope="col">{t("fuse.colAmp")}</th><th scope="col">{t("fuse.colWhat")}</th></tr>
      </thead>
      <tbody>
        {fuses.map((f, i) => {
          const key = fuseColourKey(f.amp);
          return (
            <tr key={`${f.no}-${i}`} className={f.amp ? undefined : "fuse-reserve"}>
              <td className="fuse-no">{f.no}</td>
              <td className="fuse-amp">
                {f.amp ? (
                  <>
                    <span className="fuse-dot" style={{ background: key ? FUSE_COLOURS[key] : "#FFFFFF" }} />
                    {t("fuse.amp", { amp: f.amp })}
                  </>
                ) : "–"}
              </td>
              <td>{f.what || t("fuse.reserve")}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
